/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */

import {
  findIndex,
  assign,
  pick,
} from 'lodash-es';

import Templates from '../../services/templates';

const state = {
  previewTicker: [],
  selectedElements: [],
  selectedGroups: new Set(),
  loading: false,
  isSaving: false,
  animationToggle: true,
  runLoopAnimations: true,
  textEditing: false,
  isResizing: false,

  // Dashboard Vars
  loadingTemplates: false,
  templates: [],
  page: 1,
  total: 0,
  sortBy: 'created_time',
  sortDir: 'desc',
  tags: [],
};

const getters = {
  isPreviewingTicker: (state) => (id) => state.previewTicker.includes(id),
};

const actions = {
  updateTickerPreviewing({ state, dispatch }, payload) {
    if (state.previewTicker.includes(payload)) {
      dispatch('previewTickerOff', payload);
    } else {
      dispatch('previewTickerOn', payload);
    }
  },
  previewTickerOn({ commit }, payload) {
    commit('ADD_ID_TO_PREVIEW_TICKER_ARRAY', payload);
  },
  previewTickerOff({ commit }, payload) {
    commit('REMOVE_ID_FROM_PREVIEW_TICKER_ARRAY', payload);
  },
  setPage(context, page) {
    context.commit('SET_PAGE', page);
    return context.dispatch('getTemplates');
  },

  setSortBy(context, sortBy) {
    context.commit('SET_SORT_BY', sortBy);
    return context.dispatch('getTemplates');
  },

  setSortDir(context, sortDir) {
    context.commit('SET_SORT_DIR', sortDir);
    return context.dispatch('getTemplates');
  },

  addTag(context, tag) {
    context.commit('ADD_TAG', tag);
    context.commit('SET_PAGE', 1);
    return context.dispatch('getTemplates');
  },

  removeTag(context, tag) {
    context.commit('REMOVE_TAG', tag);
    context.commit('SET_PAGE', 1);
    return context.dispatch('getTemplates');
  },

  resetTags(context) {
    context.commit('RESET_TAGS');
    context.commit('SET_PAGE', 1);
    return context.dispatch('getTemplates');
  },

  setResizing(context, payload) {
    context.commit('SET_RESIZING', payload);
  },

  /**
   * Update a single template in the templates list
   */
  updateTemplate(context, payload) {
    context.commit('UPDATE_TEMPLATE', payload);
  },

  selectElements(context, arr) {
    context.commit('SET_SELECTED_ELEMENTS', arr);
  },

  updateSelectedElements(context, element) {
    context.commit('UPDATE_SELECTED_ELEMENTS', element);
  },

  clearSelectedGroups(context) {
    context.commit('CLEAR_SELECTED_GROUPS');
  },

  selectGroup(context, payload) {
    context.commit('ADD_SELECTED_GROUP', payload);
  },

  unselectGroup(context, payload) {
    context.commit('REMOVE_SELECTED_GROUP', payload);
  },

  toggleAnimation(context) {
    context.commit('TOGGLE_ANIMATION');
  },

  setTextEditing(context, bool) {
    context.commit('SET_TEXT_EDITING', bool);
  },

  getTemplate(context, templateId) {
    return Templates.getTemplate(templateId);
  },

  async getTemplates(context) {
    const {
      page,
      sortBy,
      sortDir,
      tags,
    } = context.state;

    const opts = {
      page,
      sortBy,
      sortDir,
      tags,
    };

    context.commit('SET_LOADING_TEMPLATES', true);

    try {
      const data = await Templates.getTemplates(opts);
      context.commit('RECEIVE_TEMPLATES', data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error fetching user templates', err);
    }

    context.commit('SET_LOADING_TEMPLATES', false);
  },

  async duplicateTemplate(context, templateId) {
    let template = await Templates.getTemplate(templateId);
    if (!template) {
      throw new Error('Failed to duplicate template: Template not found.');
    }

    template = await Templates.createTemplate({
      ...template,
      title: `${template.title} - COPY`,
    });

    context.commit('CREATED', template);
  },

  async createTemplate(context, template) {
    context.commit('SET_SAVING', true);

    try {
      const data = await Templates.createTemplate(template);
      context.commit('CREATED', assign(template, pick(data, ['_id', 'created_by', 'created_time'])));
      return template._id;
    } finally {
      context.commit('SET_SAVING', false);
    }
  },

  async editTemplate(context, template) {
    context.commit('SET_SAVING', true);

    try {
      await Templates.updateTemplate(template._id, template);
      context.commit('EDITED', template);
    } finally {
      context.commit('SET_SAVING', false);
    }
  },

  deleteTemplate(context, template) {
    return Templates.deleteTemplate(template._id).then(() => {
      context.commit('DELETED', template);
    });
  },

  startLoader(context) {
    context.commit('START_LOADER');
  },

  stopLoader(context) {
    context.commit('STOP_LOADER');
  },
};

const mutations = {
  ADD_ID_TO_PREVIEW_TICKER_ARRAY(state, payload) {
    state.previewTicker.push(payload);
  },
  REMOVE_ID_FROM_PREVIEW_TICKER_ARRAY(state, payload) {
    state.previewTicker = state.previewTicker.filter((t) => t !== payload);
  },
  SET_PAGE(state, page) {
    state.page = page;
  },

  SET_SORT_BY(state, sortBy) {
    state.sortBy = sortBy;
  },

  SET_SORT_DIR(state, sortDir) {
    state.sortDir = sortDir;
  },

  ADD_TAG(state, tag) {
    if (!state.tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      state.tags.push(tag);
    }
  },

  REMOVE_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  RESET_TAGS(state) {
    state.tags = [];
  },

  SET_LOADING_TEMPLATES(state, loadingTemplates) {
    state.loadingTemplates = loadingTemplates;
  },

  SET_RESIZING(state, payload) {
    state.isResizing = payload;
  },

  SET_SELECTED_ELEMENTS(state, arr) {
    state.selectedElements = arr;
    state.textEditing = false;
  },

  UPDATE_SELECTED_ELEMENTS(state, elementId) {
    const index = state.selectedElements.indexOf(elementId);
    if (index === -1) {
      state.selectedElements.push(elementId);
    } else {
      state.selectedElements.splice(index, 1);
    }
  },

  CLEAR_SELECTED_GROUPS(state) {
    const { selectedGroups } = state;
    selectedGroups.clear();

    // Updating the Set in place isn't reactive... so create a new one
    state.selectedGroups = new Set(selectedGroups);
  },

  ADD_SELECTED_GROUP(state, id) {
    const { selectedGroups } = state;
    selectedGroups.add(id);

    // Updating the Set in place isn't reactive... so create a new one
    state.selectedGroups = new Set(selectedGroups);
  },

  REMOVE_SELECTED_GROUP(state, id) {
    const { selectedGroups } = state;
    selectedGroups.delete(id);

    // Updating the Set in place isn't reactive... so create a new one
    state.selectedGroups = new Set(selectedGroups);
  },

  SET_TEXT_EDITING(state, bool) {
    state.textEditing = bool;
  },
  TOGGLE_ANIMATION(state) {
    state.animationToggle = !state.animationToggle;
  },
  TOGGLE_LOOPING_ANIMATIONS(state, bool) {
    state.runLoopAnimations = bool;
  },
  RECEIVE_TEMPLATES(state, payload) {
    const {
      templates,
      page,
      total,
    } = payload;

    state.templates = templates;
    state.page = page;
    state.total = total;
  },

  UPDATE_TEMPLATE(state, template) {
    state.templates = state.templates.map((t) => (
      t._id === template._id
        ? template // Updated template
        : t // Existing template
    ));
  },
  CREATED(state, payload) {
    state.templates.unshift(payload);
  },
  EDITED(state, payload) {
    const index = findIndex(state.templates, (o) => o._id === payload._id);
    state.templates[index] = payload;
  },
  DELETED(state, payload) {
    const index = findIndex(state.templates, (o) => o._id === payload._id);
    state.templates.splice(index, 1);
  },

  START_LOADER(state) {
    state.loading = true;
  },

  STOP_LOADER(state) {
    state.loading = false;
  },

  SET_SAVING(state, isSaving) {
    state.isSaving = isSaving;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
